import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '../use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useGetSiteConfigs } from '../use-site-configs';
import { ApiBackendInfo } from '../athena/types';
import { GoldenDuelSetting } from './types';

/**
 * 取得 GoldenDuel 活動設定
 * @param apiBackendInfo 
 * @returns GoldenDuelSetting
 */
const fetchGoldenDuelSetting = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<GoldenDuelSetting>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/GoldenDuel/GetSettingV2`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useGoldenDuelSetting = () => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.GOLDEN_DUEL_SETTING,
			apiBackendInfo.url
		], 
		queryFn: () => fetchGoldenDuelSetting(apiBackendInfo),
		staleTime: 3 * 60 * 1000,    // 3 minutes
		cacheTime: 3 * 60 * 1000,   // 3 minutes
		enabled: Boolean(siteConfig?.EnableGoldenDuel) && apiBackendInfo.isReady
	});

	const isGoldenDuelSupported = (sportId: number, leagueId: number) => {
		const setting = data?.Data;
		return Boolean(
			siteConfig?.EnableGoldenDuel &&
				setting?.SportId === sportId &&
				setting?.LeagueIds?.includes(leagueId) 
		);
	};

	const convertAmountByCurrency = (amount: number, userCurrencyId: number) => {
		const currId = userCurrencyId ?? 2; // default to RM (馬幣)
		const currencySetting = data?.Data?.CurrencySettings?.find(setting => setting.CurrencyId === currId);
		return amount / (currencySetting?.ActualRate ?? 1);
	};

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch,
		isGoldenDuelSupported,
		convertAmountByCurrency
	};
};